import { useCallback, useState } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { LIMITS, REGEX_COMPILED } from '@weblite/permission'
// Material
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
// Common
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { DescriptionBox } from '$common/description-box'
import { UsernameInput, UsernameStatus } from '$common/input'
import { showDiscardChangesConfirmModal } from '$comp/modals/confirmation-dialog'
import { closeEditUsername } from '$logic/router'
import { getClientUser, updateClientUsername } from '$logic/client-user'
// Localization
import { t } from '$i18n'
import { areEqualStr } from '$func/text'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'

const { userConstraints } = LIMITS

const StyledBox = styled(Box)(({ theme }) => ({
  padding: '20px',
  backgroundColor: theme.palette.white,
  boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.05)',
}))

export function EditUsername() {
  const user = getClientUser()
  const [username, setUsername] = useState(user.username || '')
  const [status, setStatus] = useState<UsernameStatus>()

  const valueChanged = !areEqualStr(username, user.username || '')

  const onConfirm = useCallback(async () => {
    if (!valueChanged) return

    if (username.length && !/[a-z]/i.test(username[0]))
      setStatus('NOT_START_WITH_LETTER')
    else if (username.length && username.length < 5)
      setStatus('LESS_THAN_5_CHAR')
    else if (username.length && !REGEX_COMPILED.username.test(username))
      setStatus('INVALID')
    else {
      setStatus(undefined)
      showSaveChangesLoadingModal()
      // eslint-disable-next-line no-unneeded-ternary
      const res = await updateClientUsername(username ? username : null)
      await closeCurrentModal()
      if (res === 'AVAILABLE') closeEditUsername()
      else setStatus(res)
    }
  }, [username, valueChanged])

  const onBack = useCallback(() => {
    if (valueChanged)
      showDiscardChangesConfirmModal(closeEditUsername, onConfirm)
    else closeEditUsername()
  }, [onConfirm, valueChanged])

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={t('user.username')}
          confirmDisabled={false}
          onBack={onBack}
          onConfirm={onConfirm}
        />
      }
    >
      <StyledBox>
        <UsernameInput
          value={username}
          setValue={setUsername}
          status={status}
          maxLength={userConstraints.username.maxLength}
        />
        <DescriptionBox>{t('editUsername.descriptionBox')}</DescriptionBox>
      </StyledBox>
    </PageWithAppBar>
  )
}
